/**
 * Network utility functions for handling device network interfaces
 */

/**
 * Get all IP addresses formatted as "interface: address" on separate lines
 * Example: "eth0: 192.168.1.100\nwlan0: 10.0.0.50"
 *
 * @param {Object} node - Node object with events.system.metrics.network_interfaces
 * @returns {string} Formatted string with all interfaces and addresses
 */
export const getAllIPAddresses = (node) => {
  if (!node || !node.events || !node.events.system ||
      !node.events.system.metrics || !node.events.system.metrics.network_interfaces) {
    return '';
  }

  const interfaces = node.events.system.metrics.network_interfaces;
  const result = [];

  // Sort interfaces by name for consistent display
  const sortedInterfaceNames = Object.keys(interfaces).sort();

  for (const ifaceName of sortedInterfaceNames) {
    if (interfaces[ifaceName] && interfaces[ifaceName].address) {
      result.push(`${ifaceName}: ${interfaces[ifaceName].address}`);
    }
  }

  return result.join('\n');
};

/**
 * Get the primary IP address from available network interfaces
 * Checks interfaces in priority order: eth0, wlan0, wlan1, mlan0, mlan1
 * Use this when you need just ONE IP address for backward compatibility
 *
 * @param {Object} node - Node object with events.system.metrics.network_interfaces
 * @returns {string} IP address or empty string if none found
 */
export const getPrimaryIPAddress = (node) => {
  if (!node || !node.events || !node.events.system ||
      !node.events.system.metrics || !node.events.system.metrics.network_interfaces) {
    return '';
  }

  const interfaces = node.events.system.metrics.network_interfaces;

  // Priority order: eth0 (wired) > wlan0 (wifi) > wlan1 > mlan0 > mlan1 > any other
  const priorityOrder = ['eth0', 'wlan0', 'wlan1', 'mlan0', 'mlan1'];

  // Check priority interfaces first
  for (const ifaceName of priorityOrder) {
    if (interfaces[ifaceName] && interfaces[ifaceName].address) {
      return interfaces[ifaceName].address;
    }
  }

  // If no priority interface found, return first available interface with an address.
  // Use sorted own keys so the fallback is deterministic and ignores inherited props.
  for (const ifaceName of Object.keys(interfaces).sort()) {
    if (interfaces[ifaceName] && interfaces[ifaceName].address) {
      return interfaces[ifaceName].address;
    }
  }

  return '';
};

/**
 * Get all network interfaces with their IP and MAC addresses as an array
 *
 * @param {Object} node - Node object with events.system.metrics.network_interfaces
 * @returns {Array} Array of {interface: string, address: string, mac: string} objects, sorted by interface name
 */
export const getAllNetworkInterfaces = (node) => {
  if (!node || !node.events || !node.events.system ||
      !node.events.system.metrics || !node.events.system.metrics.network_interfaces) {
    return [];
  }

  const interfaces = node.events.system.metrics.network_interfaces;
  const result = [];

  // Sort by interface name for consistent display
  const sortedInterfaceNames = Object.keys(interfaces).sort();

  for (const ifaceName of sortedInterfaceNames) {
    const iface = interfaces[ifaceName];
    if (iface && iface.address) {
      result.push({
        interface: ifaceName,
        address: iface.address,
        mac: iface.mac || iface.hwaddr || iface.hardware_address || ''  // Handle different field names
      });
    }
  }

  return result;
};
